import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { flattenFolderTree } from '~/components/SearchV2/SearchV2.utils';
import { Button } from '~/eds/src/components/Button/Button';
import { Chip } from '~/eds/src/components/Chip/Chip';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { FieldType } from '~/evifields/src/types/FieldType';
import { Filter } from '~/evifields/src/types/Filter';
import { OperatorId } from '~/evifields/src/types/OperatorId';
import { SearchQuery } from '~/features/advanced-search/types';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { FlattenFolderTree } from '~/types/Automation';
import { SearchFilter } from '~/types/DashboardV2';
import { DocumentGroup } from '~/types/DocumentGroup';
import { FolderTree } from '~/types/FolderTree';
import { BooleanQueryEntity, QueryOperatorEntity } from '~/types/OpenSearch';

import {
  mapQuerytoSectionFiltersOperators,
  QueryFilters,
  SectionFilters,
} from './utils';

type Props = {
  isQueryBuilderSupported?: boolean;
  queryBuilder: SearchQuery;
  searchFilters: Record<string, SearchFilter>;
  onClear?: () => void;
  onEdit?: () => void;
  onOpenQueryBuilder?: () => void;
};

export const ReadOnlyComplexQuery = ({
  isQueryBuilderSupported = true,
  searchFilters,
  queryBuilder,
  onClear,
  onEdit,
  onOpenQueryBuilder,
}: Props) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const formatDate = useFormatDate();
  const { data: documentGroupsData } =
    api.endpoints.getAllDocumentGroups.useQuery(undefined);
  const { data: folderTreeData } =
    api.endpoints.getFolderTree.useQuery(undefined);
  const folders = folderTreeData?.children;
  const folderTree = useMemo(
    () => (!folders ? [] : flattenFolderTree(folders as FolderTree[])),
    [folders],
  );

  const queryFilters = mapQuerytoSectionFiltersOperators(
    queryBuilder,
    searchFilters,
  );

  const getValueByType = (opId: string, type: FieldType, value: any) => {
    switch (type) {
      case 'clause':
        if (
          value &&
          value[0] &&
          value[0].text_search &&
          value[0].text_search[0]
        ) {
          const r = value[0].text_search[0];
          return `${value[0].provision} and ${r.scope.label} ${r.text}`;
        }
        return value[0]?.provision;
      case 'date':
        if (opId === 'date_in_the_last' || opId === 'date_in_the_next') {
          return `${value[0].value} ${value[0].unit}`;
        } else if (opId === 'is_blank') {
          return '';
        } else {
          return value.map((date: Date) => formatDate(date, 'iso')).join(' - ');
        }
      case 'enum_set':
        return value.join(' - ');
      case 'number':
        const numericValue = value[0];
        if (numericValue === undefined) return value;
        return formatNumber(value);
      default:
        return value;
    }
  };

  const getValueByFieldTypeValue = (
    fieldId: string,
    opId: OperatorId,
    type: FieldType,
    values: Filter['values'],
  ) => {
    switch (fieldId) {
      case 'document_group_id':
        const groupValues = getValueByType(opId, type, values).split(' - ');
        const groupNames = groupValues.map((item: string) => {
          const result = [];
          const group = documentGroupsData?.results.find(
            (grp: DocumentGroup) => item === grp.groupId.toString(),
          );
          if (group) {
            result.push(group.name);
          }
          return result;
        });
        return groupNames?.join(', ');
      case 'folder':
      case 'folder_Document':
        const folderValues = getValueByType(opId, 'folder', values);
        const folderNames = folderValues.map((item: string) => {
          const result = [];
          const folder = folderTree?.find(
            (folder: FlattenFolderTree) => item === folder.value,
          );
          if (folder) {
            result.push(folder.display_value);
          }
          return result;
        });
        return folderNames.join(', ');
      default:
        return getValueByType(opId, type, values);
    }
  };

  const testIsSectionFilter = (
    sectionItem: QueryFilters,
  ): sectionItem is SectionFilters =>
    'type' in sectionItem && sectionItem.type === 'section';

  const testIsOperatorFilter = (
    sectionItem: QueryFilters,
  ): sectionItem is QueryOperatorEntity =>
    'type' in sectionItem && sectionItem.type === 'operator';

  const testIsBooleanTextSearch = (
    sectionItem: QueryFilters,
  ): sectionItem is BooleanQueryEntity =>
    'type' in sectionItem && sectionItem.type === 'bool_text_search';

  const renderFieldValueComponent = (
    tooltip: string,
    label: string,
    key: React.Key,
  ) => (
    <Layout align="center" key={key}>
      <Tooltip tooltip={tooltip}>
        <Layout
          p={1}
          styles={{
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            borderRadius: '4px',
          }}
        >
          <Text variant="tiny">{label}</Text>
        </Layout>
      </Tooltip>
    </Layout>
  );

  const renderFilter = (filter: Filter, index: number) => {
    const searchFilter = searchFilters[filter.fieldId];
    if (searchFilter) {
      const type = searchFilters[filter.fieldId].type;
      const opId = filter.operatorId!;

      const operatorValue =
        searchFilters[filter.fieldId].operators.find((op) => op.id === opId)
          ?.label ?? '';
      const value = getValueByFieldTypeValue(
        filter.fieldId,
        opId,
        type,
        filter.values,
      );
      const tooltip = `${
        searchFilters[filter.fieldId].label
      } ${operatorValue} ${value}`;
      const key = `${searchFilters[filter.fieldId].label}-${
        filter.operatorId
      }-${index}`;
      const label = searchFilters[filter.fieldId.toString()].label;
      return renderFieldValueComponent(tooltip, label, key);
    } else {
      return (
        <Tooltip
          tooltip={intl.formatMessage({
            id: 'ReadOnlyComplexQuery.removedFilterTooltip',
            defaultMessage:
              'This field has been deleted. Edit Query to remove.',
            description:
              'Tooltip for removed filter in read-only complex query',
          })}
        >
          <Layout
            onClick={() => {
              onOpenQueryBuilder?.();
            }}
          >
            <Chip
              text={intl.formatMessage({
                id: 'ReadOnlyComplexQuery.removedFilter',
                defaultMessage: 'Remove Filter',
                description:
                  'Text for removed filter in read-only complex query',
              })}
              status="danger"
            />
          </Layout>
        </Tooltip>
      );
    }
  };

  const renderBooleanTextSearch = (
    filter: BooleanQueryEntity,
    index: number,
  ) => {
    const tooltip = filter.value ?? '';
    const key = `${filter.value}-${index}`;
    const label = intl.formatMessage({
      id: 'ReadOnlyComplexQuery.booleanTextSearchLabel',
      defaultMessage: 'Boolean Text Search',
      description: 'Label for boolean text search filter',
    });
    return renderFieldValueComponent(tooltip, label, key);
  };

  const renderOperator = (operator: QueryOperatorEntity, index: number) => (
    <Layout key={`item_${index}`} align="center" pl={3} pr={3}>
      <Text variant="tiny-bold">
        {operator.value === 'and'
          ? intl.formatMessage({
              id: 'ReadOnlyComplexQuery.andOperator',
              defaultMessage: 'AND',
              description: 'Label for AND operator in read-only complex query',
            })
          : intl.formatMessage({
              id: 'ReadOnlyComplexQuery.orOperator',
              defaultMessage: 'OR',
              description: 'Label for OR operator in read-only complex query',
            })}
      </Text>
    </Layout>
  );

  const renderEntities = (sectionItem: QueryFilters, index: number) => {
    if (testIsSectionFilter(sectionItem)) {
      return (
        <Layout key={`section_${index}`} align="center">
          <Layout pr={3}>(</Layout>
          {sectionItem.values.map(renderEntities)}
          <Layout pl={3}>)</Layout>
        </Layout>
      );
    } else if (testIsOperatorFilter(sectionItem)) {
      return renderOperator(sectionItem, index);
    } else if (testIsBooleanTextSearch(sectionItem)) {
      return renderBooleanTextSearch(sectionItem, index);
    } else {
      return renderFilter(sectionItem, index);
    }
  };

  const queryMemoized = useMemo(
    () => queryFilters?.map(renderEntities),
    [queryFilters],
  );

  return (
    <Layout align="center" spacing={3} wrap={true} justify="space-between">
      <Layout>
        <Layout wrap>{queryMemoized}</Layout>

        {onEdit && (
          <Button
            text={intl.formatMessage({
              id: 'ReadOnlyComplexQuery.editQueryButton',
              defaultMessage: 'Edit Query',
              description: 'Button text to edit query',
            })}
            variant="action"
            onClick={onEdit}
            disabled={!isQueryBuilderSupported}
            tooltip={
              !isQueryBuilderSupported
                ? intl.formatMessage({
                    id: 'ReadOnlyComplexQuery.queryNotSupportedTooltip',
                    defaultMessage:
                      'Query not supported to be edited by query builder.',
                    description: 'Tooltip when query cannot be edited',
                  })
                : ''
            }
          />
        )}
      </Layout>

      {onClear && (
        <Button
          text={intl.formatMessage({
            id: 'ReadOnlyComplexQuery.resetFiltersButton',
            defaultMessage: 'Reset Filters',
            description: 'Button text to reset all filters',
          })}
          icon="reload"
          iconPosition="left"
          variant="action"
          onClick={onClear}
        />
      )}
    </Layout>
  );
};
